import React, { useState } from "react";
import { Row, Col, Collapse } from "react-bootstrap";
import { Funnel } from "react-bootstrap-icons";

const DailySalesPerformanceDetailed = ({ data, onICNameClick, onTotalRowClickThrough, onBeatPlanAdherenceClick, onFilterChange, activeFilter, onMTDAcquisitionClick, onMTDBeatPlanGTVClick }) => {

    const [showFilters, setShowFilters] = useState(false);

    // Map UI filter names to API filter_type values
    const getFilterType = (uiFilter) => {
        const filterMap = {
            'bottom10': 'bottom_10',
            'beatAdherence80Plus': 'beat_adherence_above_80',
            'beatAdherenceBelow80': 'beat_adherence_below_80',
            'acquisition80Plus': 'mtd_acquisition_above_80',
            'acquisitionBelow80': 'mtd_acquisition_below_80',
            'gtv80Plus': 'beat_plan_gtv_above_80',
            'gtvBelow80': 'beat_plan_gtv_below_80',
            'overall80Plus': 'overall_achievement_above_80',
            'overallBelow80': 'overall_achievement_below_80'
        };
        return filterMap[uiFilter] || null;
    };

    // Handle filter change
    const handleFilterClick = (filterName) => {
        const newFilter = activeFilter === filterName ? null : filterName;

        // Notify parent component of filter change
        if (onFilterChange) {
            const filterType = newFilter ? getFilterType(newFilter) : null;
            onFilterChange(filterType, newFilter);
        }
    };

    if (!data) return null;

    const { managerName, department, ocrWeightage, summaryCounts, ics, totals } = data;

    // Filter ICs based on active filter
    const getFilteredICs = () => {
        if (!activeFilter) return ics;
        // This is a placeholder - Will need to implement the actual filter logic based on data required
        return ics;
    };

    const filteredICs = getFilteredICs();

    const filters = [
        { name: 'bottom10', label: '# ICs In Bottom 10' },
        { name: 'beatAdherence80Plus', label: '# ICs Meeting >= 80% Beat Adherence Target' },
        { name: 'beatAdherenceBelow80', label: '# ICs Meeting < 80% Beat Plan Adherence Target' },
        { name: 'acquisition80Plus', label: '# ICs Meeting >= 80% MTD Acquisition' },
        { name: 'acquisitionBelow80', label: '# ICs Meeting < 80% MTD Acquisition' },
        { name: 'gtv80Plus', label: '# ICs Meeting >= 80% GTV from Beat Visit Target' },
        { name: 'gtvBelow80', label: '# ICs Meeting < 80% GTV from Beat Visit Target' },
        { name: 'overall80Plus', label: '# ICs With Overall Achievement >= 80%' },
        { name: 'overallBelow80', label: '# ICs With Overall Achievement < 80%' },
    ];


    return (
        <div className="spd-table-container">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h4 className="mt-4 mb-3">{managerName}</h4>

                <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="btn btn-outline-secondary btn-filter"
                    aria-controls="filter-collapse"
                    aria-expanded={showFilters}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '6px 12px',
                        fontSize: '0.85rem',
                        flexShrink: 0,
                        borderColor: '#6c757d'
                    }}
                >
                    <Funnel className="me-2" size={14} />
                    Filters
                </button>
            </div>

            <Collapse in={showFilters}>
                <div id="filter-collapse" className="mb-3 p-3 border rounded spd-dashboard-filters">
                    <Row className="g-2">
                        {filters.map((filter) => (
                            <Col xs={12} sm={6} md={4} lg={3} xl={2} key={filter.name} className="mb-2">
                                <div
                                    className={`spd-filter-cell p-2 text-center border rounded ${activeFilter === filter.name ? 'active' : ''}`}
                                    onClick={() => handleFilterClick(filter.name)}
                                    style={{ cursor: 'pointer', height: '100%', whiteSpace: 'normal', minHeight: '50px' }}
                                >
                                    {filter.name === 'bottom10' && '# ICs In Bottom 10'}
                                    {filter.name === 'beatAdherence80Plus' && '# ICs Meeting >= 80% Beat Adherence Target'}
                                    {filter.name === 'beatAdherenceBelow80' && '# ICs Meeting < 80% Beat Plan Adherence Target'}
                                    {filter.name === 'acquisition80Plus' && '# ICs Meeting >= 80% MTD Acquisition'}
                                    {filter.name === 'acquisitionBelow80' && '# ICs Meeting < 80% MTD Acquisition'}
                                    {filter.name === 'gtv80Plus' && '# ICs Meeting >= 80% GTV from Beat Visit Target'}
                                    {filter.name === 'gtvBelow80' && '# ICs Meeting < 80% GTV from Beat Visit Target'}
                                    {filter.name === 'overall80Plus' && '# ICs With Overall Achievement >= 80%'}
                                    {filter.name === 'overallBelow80' && '# ICs With Overall Achievement < 80%'}
                                </div>
                            </Col>
                        ))}
                    </Row>
                </div>
            </Collapse>

            <div className="table-responsive">
                <table className="table table-bordered">
                    <thead>

                        {/* Period Row */}
                        <tr className="spd-period-row">
                            <th rowSpan="2">IC Name</th>
                            <th rowSpan="2">Manager Name</th>
                            <th rowSpan="2">Total Allocated<br />UTIDs</th>
                            <th colSpan="2">Beat Plan Adherence</th>
                            <th colSpan="2">MTD Acquisition</th>
                            <th colSpan="1">MTD Beat Plan GTV</th>
                            <th colSpan="1">MTD OKR</th>
                        </tr>
                        {/* Column Headers */}
                        <tr>
                            <th>Live (Today)</th>
                            <th>MTD</th>
                            <th>Target</th>
                            <th>Achieved</th>
                            <th>% Achieved</th>
                            <th>% Achieved</th>
                        </tr>
                    </thead>
                    <tbody>
                        {/* OKR Weightage Row */}
                        <tr className="spd-okr-weightage-row">
                            <td><strong>OKR Weightage</strong></td>
                            <td></td>
                            <td></td>
                            <td colSpan="2"></td>
                            <td colSpan="2"><strong>{ocrWeightage.acquisition || 'N/A'}</strong></td>
                            <td colSpan="1"><strong>{ocrWeightage.beatPlanGTV || 'N/A'}</strong></td>
                            <td colSpan="1"></td>
                        </tr>
                        {filteredICs.map((ic, idx) => (
                            <tr key={idx}>
                                <td>
                                    <a
                                        href="#"
                                        className="spd-ic-link"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            onICNameClick && onICNameClick(ic.icId);
                                        }}
                                    >
                                        {ic.icName}
                                    </a>
                                </td>
                                <td>{ic.managerName}</td>
                                <td className="text-center">{ic.totalAllocatedUTIDs}</td>
                                <td className="text-center">
                                    {ic.beatPlanAdherence.liveToday}%
                                </td>
                                <td
                                    className="text-center spd-clickable-cell"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onBeatPlanAdherenceClick && onBeatPlanAdherenceClick(ic);
                                    }}
                                    style={{ cursor: 'pointer', color: '#007bff' }}
                                    title="Click to view Beat Plan Adherence Report"
                                >
                                    {ic.beatPlanAdherence.mtd}%
                                </td>
                                <td
                                    className="text-center"
                                >
                                    {ic.mtdAcquisition.target}
                                </td>
                                <td className="text-center spd-clickable-cell"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onMTDAcquisitionClick && onMTDAcquisitionClick(ic);
                                    }}
                                    style={{ cursor: 'pointer', color: '#007bff' }}
                                    title="Click to view Acquisition Report">{ic.mtdAcquisition.achieved}</td>
                                <td
                                    className="text-center spd-clickable-cell"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onMTDBeatPlanGTVClick && onMTDBeatPlanGTVClick(ic);
                                    }}
                                    style={{ cursor: 'pointer', color: '#007bff' }}
                                    title="Click to view Beat Plan GTV Report"
                                >
                                    {ic.mtdBeatPlanGTV.achievedPercent}%
                                </td>
                                <td
                                    className="text-center"
                                >
                                    {ic.mtdOKR.percentage}%
                                </td>
                            </tr>
                        ))}

                        <tr
                            className="spd-total-row"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onTotalRowClickThrough && onTotalRowClickThrough();
                            }}
                            style={{ cursor: 'pointer' }}
                        >
                            <td><strong>Total</strong></td>
                            <td></td>
                            <td className="text-center"><strong>{totals.totalAllocatedUTIDs}</strong></td>
                            <td className="text-center"><strong>{totals.beatPlanAdherence.liveToday}%</strong></td>
                            <td className="text-center"><strong>{totals.beatPlanAdherence.mtd}%</strong></td>
                            <td className="text-center"><strong>{totals.mtdAcquisition.target}</strong></td>
                            <td className="text-center"><strong>{totals.mtdAcquisition.achieved}</strong></td>
                            <td className="text-center"><strong>{totals.mtdBeatPlanGTV.achievedPercent}%</strong></td>
                            <td className="text-center"><strong>{totals.mtdOKR.percentage}%</strong></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="mt-2 text-muted small">
                <em>Click on IC name to view stores in Team Dashboard • Click Beat Plan Adherence (MTD) to view detailed report • Click Total row to view all stores in Team Dashboard</em>
            </div>
        </div>
    );
};

export default DailySalesPerformanceDetailed;